import React, { useEffect, useRef, useState } from "react";
import {
  useStripe,
  useElements,
  PaymentElement,
  ExpressCheckoutElement,
} from "@stripe/react-stripe-js";
import { toast } from "react-hot-toast";
import { CreditCard } from "lucide-react";
import { BASE_URL } from "../utils/tools";
import { Spinner } from "@jammable/ui-components/Loader";
import { useT } from "@jammable/ui-core/src/i18n";

// intentType "setup" confirms a SetupIntent (free-trial checkout: card saved
// now, first charge after the trial) instead of paying a PaymentIntent.
// `express` puts Apple Pay / Google Pay, Link and PayPal buttons above the
// card fields (and takes the wallets out of the card form's tabs). It is on
// by default; it used to be only for people coming back to checkout.
//
// Until both Stripe elements say they're ready, a skeleton the size of the
// form stands in for them and the pay button is disabled. Before, Stripe's
// own loader sat in a short iframe that grew as it loaded, the wallet row
// appeared above it afterwards, and the pay button could be tapped before
// the form existed or again while a payment was confirming.
//
// Preloaded (`deferred`, Stripe's deferred-intent Elements): the form is
// drawn from the plan's price before any subscription exists, so it can sit
// mounted out of sight on the paywall and be there the moment a plan is
// picked. `clientSecret` is then a promise for the subscription's secret
// (PlanCTA starts it on the click); paying checks the fields, waits for it,
// moves the form onto the subscription's real amount if the preview was
// off, and confirms that intent. Without `deferred` it's the old
// intent-first form and `clientSecret` is the string.
const StripePaymentElement = ({
  price,
  submitLabel,
  // Optional layout hooks (the full-screen paywall pins the button to the
  // bottom of the screen with its terms under it).
  formClassName = "",
  submitWrapClassName = "",
  footer = null,
  onReady,
  discount,
  currency,
  intentType = "payment",
  express = true,
  dark = false,
  deferred = false,
  clientSecret = null,
}) => {
  const stripe = useStripe();
  const t = useT();
  const [loading, setLoading] = useState(false);

  const [error, setError] = useState(null);
  const [walletsShown, setWalletsShown] = useState(false);
  const [cardReady, setCardReady] = useState(false);
  const [expressReady, setExpressReady] = useState(!express);
  const elements = useElements();

  // Wallets that never report (blocked by an extension, slow network) don't
  // hold the card form back for more than a couple of seconds.
  useEffect(() => {
    if (!cardReady || expressReady) return;
    const timer = setTimeout(() => setExpressReady(true), 2500);
    return () => clearTimeout(timer);
  }, [cardReady, expressReady]);
  const ready = cardReady && expressReady;
  useEffect(() => {
    if (ready) onReady?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  // The secret (or the promise of it) this render was given, read when
  // paying rather than when the handler was made.
  const secretRef = useRef(clientSecret);
  secretRef.current = clientSecret;
  // The amount and currency the preloaded form was last moved onto.
  const synced = useRef(null);

  // Puts the preloaded form on the payment intent's own amount and currency
  // (what Apple Pay's sheet shows). `source` is what the secret came from,
  // so a plan changed meanwhile isn't overwritten. True when it updated.
  const follow = async (secret, source) => {
    if (intentType === "setup" || synced.current === secret) return false;
    const { paymentIntent } = await stripe.retrievePaymentIntent(secret);
    if (!paymentIntent || secretRef.current !== source) return false;
    synced.current = secret;
    await elements.update({
      amount: paymentIntent.amount,
      currency: paymentIntent.currency,
    });
    return true;
  };

  // As soon as the subscription exists, so a wallet tapped after this shows
  // the exact amount.
  useEffect(() => {
    if (!deferred || !clientSecret || !stripe || !elements) return;
    let live = true;
    Promise.resolve(clientSecret)
      .then((secret) => live && secret && follow(secret, clientSecret))
      .catch(() => {});
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deferred, clientSecret, stripe, elements]);

  const confirm = async () => {
    const confirmFn =
      intentType === "setup"
        ? stripe.confirmSetup.bind(stripe)
        : stripe.confirmPayment.bind(stripe);
    const confirmParams = {
      return_url: `${BASE_URL}/thankyou${intentType === "setup" ? "?trial=1" : ""}`,
    };
    if (!deferred) return confirmFn({ elements, confirmParams });

    // Stripe checks the fields first (a wallet's sheet already took them);
    // what's missing shows under the fields.
    const checked = await elements.submit();
    if (checked.error) return checked;
    const source = secretRef.current;
    let secret;
    try {
      secret = await source;
    } catch (e) {
      return {
        error: {
          message: e?.response?.data?.error || t("checkout.loadFailed"),
        },
      };
    }
    if (!secret) return { error: { message: t("checkout.loadFailed") } };
    // Still on the preview price: onto the real one, and checked again.
    if (await follow(secret, source)) {
      const again = await elements.submit();
      if (again.error) return again;
    }
    return confirmFn({ elements, clientSecret: secret, confirmParams });
  };

  // Apple Pay / Google Pay: the wallet's sheet has already taken the payment
  // details; this confirms the same intent the card form would.
  async function handleExpressConfirm(event) {
    if (!stripe || !elements) return;
    const { error: e } = await confirm();
    if (e) {
      event.paymentFailed?.({ reason: "fail" });
      if (e.type !== "validation_error") setError(e.message);
    }
  }

  async function handleCheckoutFormSubmit(event) {
    event.preventDefault();

    if (!stripe || !elements || !ready || loading) {
      return;
    }
    setLoading(true);

    const stripePayload = await confirm();
    setLoading(false);
    if (stripePayload.error) {
      setLoading(false);
      // dispatch(actions.errorGlobal(stripePayload.error.message));
      if (stripePayload?.error?.message === "Your card has been declined.") {
        setError(t("checkout.declined"));
      } else if (stripePayload.error.type === "validation_error") {
        // Stripe already says what's wrong under the field.
      } else {
        setError(stripePayload?.error?.message);
      }
    } else {
      toast.success(
        intentType === "setup"
          ? t("checkout.trialStarted")
          : t("checkout.thanks"),
      );
    }
    setLoading(false);
  }

  return (
    <form
      style={{ marginBottom: 0 }}
      className={`tw-relative ${formClassName}`}
      onSubmit={handleCheckoutFormSubmit}
    >
      {error && (
        <div className="tw-mb-4 tw-rounded-[20px] tw-bg-[#FDECEF] tw-px-4 tw-py-3 tw-text-sm tw-font-bold tw-text-[#B4123A]">
          {error}
        </div>
      )}

      {!ready && <PaymentSkeleton express={express} dark={dark} />}

      {/* Mounted from the start so Stripe can load behind the skeleton, kept
        out of the layout until it is ready. */}
      <div
        className={
          ready
            ? ""
            : "tw-pointer-events-none tw-absolute tw-inset-x-0 tw-top-0 tw-opacity-0"
        }
        aria-hidden={!ready}
      >
        {express && (
          // Collapsed when Stripe has no wallet or Link for this device.
          <div
            className={
              walletsShown
                ? "tw-mb-4 tw-flex tw-flex-col tw-gap-3"
                : "tw-h-0 tw-overflow-hidden tw-opacity-0"
            }
          >
            <ExpressCheckoutElement
              options={{
                buttonHeight: 48,
                buttonType: {
                  applePay: "plain",
                  googlePay: "plain",
                  paypal: "paypal",
                },
                // Wallet and Link side by side where there's room (Stripe
                // stacks them below ~560px wide). Never add maxRows: 1 to
                // overflow: "never" - with both set the element never fires
                // ready and shows no buttons (tested in Chrome, 27 Oct 2026).
                layout: { maxColumns: 2, overflow: "never" },
                paymentMethods: {
                  // "always": Apple Pay on Chrome, Edge, Firefox and Opera
                  // too, not only Safari. Off an Apple device Stripe shows a
                  // QR code to finish on an iPhone. Stripe has no Apple Pay
                  // on Android browsers, so it still doesn't show there.
                  applePay: "always",
                  googlePay: "auto",
                  link: "auto",
                  // PayPal took about a fifth of payments from the card form's
                  // list, so it gets a button here too. The card form can't
                  // hide PayPal client-side, so it stays listed there as well.
                  paypal: "auto",
                  amazonPay: "never",
                },
              }}
              onReady={({ availablePaymentMethods }) => {
                setWalletsShown(
                  !!availablePaymentMethods &&
                    Object.values(availablePaymentMethods).some(Boolean),
                );
                setExpressReady(true);
              }}
              onLoadError={() => setExpressReady(true)}
              onConfirm={handleExpressConfirm}
            />
            <div className="tw-flex tw-items-center tw-gap-3 tw-text-xs tw-font-bold tw-opacity-70">
              <span className="tw-h-px tw-flex-1 tw-bg-current tw-opacity-30" />
              {t("checkout.orCard")}
              <span className="tw-h-px tw-flex-1 tw-bg-current tw-opacity-30" />
            </div>
          </div>
        )}

        <div className="tw-mb-5">
          <PaymentElement
            onReady={() => setCardReady(true)}
            // Without this a form that never loads would leave the skeleton
            // up forever; Stripe's message says what went wrong instead.
            onLoadError={({ error: e }) => {
              setCardReady(true);
              setError(e?.message || t("checkout.loadFailed"));
            }}
            onChange={() => setError(null)}
            // With the buttons on top, the card form has none of them, Link
            // included, so Link isn't offered twice (RaceCut does the same).
            options={
              express
                ? {
                    wallets: {
                      applePay: "never",
                      googlePay: "never",
                      link: "never",
                    },
                  }
                : undefined
            }
          />
        </div>
      </div>

      <div className={submitWrapClassName}>
        {/* Design language F primary CTA: lime pill, ink text, no shadow. */}
        <button
          className="tw-flex tw-h-[58px] tw-w-full tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-jam-lime tw-px-4 tw-text-[17px] tw-font-extrabold tw-text-jam-ink tw-transition hover:tw-brightness-95 active:tw-scale-[0.98] disabled:tw-cursor-not-allowed disabled:tw-opacity-50 md:tw-h-[60px]"
          type="submit"
          disabled={!ready || loading}
        >
          {loading ? (
            <Spinner className="tw-border-3 tw-border-current" size={4} />
          ) : (
            <div className="tw-flex tw-items-center tw-gap-2">
              <CreditCard size={20} strokeWidth={2.4} />
              <div>
                {submitLabel ||
                  (intentType === "setup"
                    ? t("checkout.startTrial")
                    : t("checkout.pay"))}
              </div>
            </div>
          )}
        </button>
        {footer}
      </div>
    </form>
  );
};

// The form's shape while Stripe loads: wallet buttons, the divider, then the
// card number and the two half-width rows, at the real fields' heights.
const PaymentSkeleton = ({ express, dark }) => {
  const block = `tw-animate-pulse ${dark ? "tw-bg-white/10" : "tw-bg-jam-lav"}`;
  return (
    <div className="tw-mb-5 tw-flex tw-flex-col tw-gap-3" aria-busy="true">
      {express && (
        <>
          <div className={`tw-h-12 tw-rounded-full ${block}`} />
          <div className={`tw-h-12 tw-rounded-full ${block}`} />
          <div className="tw-my-1 tw-flex tw-items-center tw-gap-3 tw-text-xs tw-font-bold tw-opacity-40">
            <span className="tw-h-px tw-flex-1 tw-bg-current tw-opacity-30" />
            <span className="tw-h-px tw-flex-1 tw-bg-current tw-opacity-30" />
          </div>
        </>
      )}
      <div className={`tw-h-[56px] tw-rounded-2xl ${block}`} />
      <div className="tw-flex tw-gap-3">
        <div className={`tw-h-[56px] tw-flex-1 tw-rounded-2xl ${block}`} />
        <div className={`tw-h-[56px] tw-flex-1 tw-rounded-2xl ${block}`} />
      </div>
      <div className="tw-flex tw-gap-3">
        <div className={`tw-h-[56px] tw-flex-1 tw-rounded-2xl ${block}`} />
        <div className={`tw-h-[56px] tw-flex-1 tw-rounded-2xl ${block}`} />
      </div>
    </div>
  );
};

export default StripePaymentElement;
